import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { KpiCard, DealCard } from "@/components/Cards";
import { PageHeader } from "@/components/DashboardLayout";
import ReferralModal from "@/components/ReferralModal";
import SignupMembershipDialog from "@/components/SignupMembershipDialog";
import { Sparkles, Bookmark, CreditCard, ArrowRight, Trophy, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCountry } from "@/lib/CountryContext";
import { formatPrice } from "@/lib/currency";

export default function Overview() {
    const { user } = useAuth();
    const { country } = useCountry();
    const [data, setData] = useState(null);
    const [breakdown, setBreakdown] = useState([]);
    const [referralOpen, setReferralOpen] = useState(false);
    // Offered once, on the first Overview render after signup. The flag is
    // read and cleared together so it survives neither a new session nor a
    // return from an abandoned Stripe checkout.
    const [offerOpen, setOfferOpen] = useState(false);

    useEffect(() => {
        api.get("/member/overview").then((r) => setData(r.data));
        api.get("/member/savings-breakdown").then((r) => setBreakdown(r.data));
    }, []);

    useEffect(() => {
        let flagged = false;
        try {
            flagged = sessionStorage.getItem("perx_signup_offer") === "1";
            sessionStorage.removeItem("perx_signup_offer");
        } catch (_e) {
            // storage disabled — no-op
        }
        if (flagged && !user?.membership_active) setOfferOpen(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!data) return <div className="p-10 text-navy-500">Loading…</div>;

    return (
        <>
            <PageHeader
                eyebrow={`Signed in as ${user?.role}`}
                title={`Hello ${user?.name?.split(" ")[0] || "friend"} 👋`}
                description={`You're on the ${data.plan.name} plan. Here's what's happening in your Perx.`}
                action={
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            onClick={() => setReferralOpen(true)}
                            data-testid="member-refer-btn"
                            className="rounded-full h-11 px-5 border-gold-300 bg-gold-50 hover:bg-gold-100 text-navy-700"
                        >
                            <Gift className="mr-2" size={16} />
                            Refer a friend
                        </Button>
                        <Link to="/member/deals">
                            <Button className="rounded-full bg-navy-700 hover:bg-navy-800 text-white h-11 px-6">
                                Browse all deals <ArrowRight className="ml-2" size={16} />
                            </Button>
                        </Link>
                    </div>
                }
                testid="member-overview-header"
            />

            <div className="px-6 lg:px-12 py-10 space-y-10">
                <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
                    <KpiCard
                        label="Membership"
                        value={data.membership_active ? "Active" : "Inactive"}
                        delta={data.plan.tagline}
                        icon={CreditCard}
                    />
                    <KpiCard
                        label="Saved deals"
                        value={data.saved_deals}
                        delta={<Link to="/member/deals" className="text-gold-600">View list →</Link>}
                        icon={Bookmark}
                    />
                    <KpiCard
                        label="Orders"
                        value={data.orders}
                        delta={<Link to="/member/payments" className="text-gold-600">Manage payments →</Link>}
                        icon={Sparkles}
                    />
                    <KpiCard
                        label="Total savings"
                        value={formatPrice(data.total_savings ?? 0, country, { decimals: 2 })}
                        delta={`vs ${formatPrice(data.total_spent ?? 0, country, { decimals: 2 })} spent`}
                        icon={Sparkles}
                        accent="pink"
                    />
                    <KpiCard
                        label="Secondary members"
                        value={data.sub_users ?? 0}
                        delta={<Link to="/member/family" className="text-gold-600">Manage family →</Link>}
                        icon={Bookmark}
                    />
                </div>

                {breakdown.length > 0 && (
                    <FamilySavings breakdown={breakdown} />
                )}

                <div>
                    <div className="flex items-end justify-between mb-6">
                        <div>
                            <div className="text-xs uppercase tracking-[0.3em] text-gold-600 mb-2">
                                Picked for you
                            </div>
                            <h2 className="font-display text-3xl text-navy-700">
                                Fresh perks the flock is loving.
                            </h2>
                        </div>
                        <Link
                            to="/member/deals"
                            className="text-sm font-semibold text-navy-700 hover:text-gold-600 inline-flex items-center gap-2"
                        >
                            All my deals <ArrowRight size={16} />
                        </Link>
                    </div>
                    <div
                        data-testid="member-recommended-grid"
                        className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5"
                    >
                        {data.recommended.map((d) => (
                            <DealCard
                                key={d.id}
                                deal={d}
                                to={`/member/deals/${d.id}`}
                            />
                        ))}
                    </div>
                </div>
            </div>
            <ReferralModal
                open={referralOpen}
                onClose={() => setReferralOpen(false)}
            />
            <SignupMembershipDialog
                open={offerOpen}
                onOpenChange={setOfferOpen}
            />
        </>
    );
}

function FamilySavings({ breakdown }) {
    const { country } = useCountry();
    const max = Math.max(1, ...breakdown.map((b) => b.savings));
    const totalSavings = breakdown.reduce((s, b) => s + b.savings, 0);
    const topSaver = [...breakdown].sort((a, b) => b.savings - a.savings)[0];
    return (
        <div
            data-testid="family-savings-panel"
            className="rounded-2xl bg-white shadow-soft p-6 lg:p-8"
        >
            <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
                <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-gold-600 mb-2">
                        Family savings split
                    </div>
                    <h2 className="font-display text-2xl text-navy-700">
                        Who&apos;s saving the most in your flock?
                    </h2>
                    <p className="text-sm text-navy-500 mt-1">
                        Every deal redemption is credited to whoever bought it — you
                        or a secondary user. Total saved across the family:{" "}
                        <strong className="text-navy-700">
                            {formatPrice(totalSavings, country, { decimals: 2 })}
                        </strong>
                        .
                    </p>
                </div>
                {topSaver && topSaver.savings > 0 && (
                    <div className="inline-flex items-center gap-2 rounded-full bg-gold-500 text-navy-900 text-xs font-semibold px-4 py-2 uppercase tracking-widest">
                        <Trophy size={14} /> {topSaver.name}
                    </div>
                )}
            </div>
            <div
                data-testid="family-savings-scroll"
                className="space-y-4 max-h-[420px] overflow-y-auto pr-3 -mr-1 scroll-smooth [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-cream [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-thumb]:bg-navy-700/40 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-navy-700/60"
            >
                {breakdown.map((b) => {
                    const pct = Math.round((b.savings / max) * 100);
                    return (
                        <div
                            key={b.id}
                            data-testid={`savings-row-${b.id}`}
                            className="flex items-center gap-4"
                        >
                            <div className="w-40 flex-shrink-0">
                                <div className="text-navy-700 font-medium truncate">
                                    {b.name}
                                </div>
                                <div className="text-[10px] uppercase tracking-widest text-navy-500 mt-0.5">
                                    {b.kind === "primary"
                                        ? "Primary member"
                                        : "Secondary member"}
                                    {" · "}
                                    {b.orders} order{b.orders === 1 ? "" : "s"}
                                </div>
                            </div>
                            <div className="flex-1 h-8 bg-cream rounded-full overflow-hidden relative">
                                <div
                                    className={`h-full rounded-full transition-all duration-500 ${
                                        b.kind === "primary"
                                            ? "bg-navy-700"
                                            : "bg-gold-500"
                                    }`}
                                    style={{
                                        width: `${b.savings > 0 ? Math.max(6, pct) : 0}%`,
                                    }}
                                />
                            </div>
                            <div className="w-32 flex-shrink-0 text-right">
                                <div className="font-display text-lg text-navy-700">
                                    {formatPrice(b.savings, country, { decimals: 2 })}
                                </div>
                                <div className="text-[10px] uppercase tracking-widest text-navy-500">
                                    saved · {formatPrice(b.spent, country, { decimals: 2 })} spent
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

